<template>
  <div class="w-full">
    <!-- Total de estructuras del ámbito (DXF total o de la capa). -->
    <div v-if="structureCount > 0"
      class="flex items-center justify-between rounded-md bg-[#eef3fb] border border-[#c9d6ef] px-3 py-1.5 mb-3">
      <span class="text-xs font-medium text-[#2f5aad]">{{ $t('total_structures') }}</span>
      <span class="text-sm font-bold text-[#2f5aad] tabular-nums">{{ structureCount }}</span>
    </div>

    <!-- En compacto los 4 campos van en grid de 2 columnas. -->
    <div :class="compact ? 'grid grid-cols-2 gap-x-3 gap-y-1 items-start' : ''">
    <!-- Candidad de ejes de la estructura, es introducido manualmente -->
    <div :class="compact ? 'mb-1' : 'mb-2'">
      <div :class="['font-medium capitalize text-blue truncate', compact ? 'text-xs' : 'text-sm']">{{ compact ? $t('axles') : 'Cantidad de ejes de la estructura' }}</div>
        <div v-if="!compact" class="mb-1 text-xs text-black">Eje(s) por estructura</div>
        <div class="flex flex-row items-center">
        <b-field class="mb-0 w-full"
          :type="{ 'is-danger' : errors.axles } "
          :message="{ 'La cantidad es invalida' : errors.axles  }">
          <b-numberinput
            v-model="form.total_axles"
            controls-position="compact"
            :disabled="disableFields"
            size="is-small"
            min="1"
            max="3"
            icon-pack="fas"
            @input="(value) => { errors.axles = value < 1 && value > 3 }"
            expanded
          />
        </b-field>
      </div>
      <p v-if="structureCount > 0" class="mt-0.5 text-[11px] text-gray-500 tabular-nums">× {{ structureCount }} = <strong class="text-gray-700">{{ cAxles }}</strong></p>
    </div>

    <!-- campo total de paneles por estructura, es introducido manualmente -->
    <div :class="compact ? 'mb-1' : 'mb-2'">
        <div :class="['font-medium capitalize text-blue truncate', compact ? 'text-xs' : 'text-sm']">{{ compact ? $t('pilings') : $t('total_pilings') }}</div>
        <div v-if="!compact" class="mb-1 text-xs text-black">(Hincados (pilotes) por eje)</div>
        <div class="flex flex-row items-center">
        <b-field class="mb-0 w-full"
          :type="{ 'is-danger' : errors.pilings } "
          :message="{ 'La cantidad es invalida' : errors.pilings  }">
          <b-numberinput
            v-model="form.total_pilings"
            controls-position="compact"
            :disabled="disableFields || !createElements.pilings"
            size="is-small"
            icon-pack="fas"
            @input="(value) => { errors.pilings = value <= 0 }"
            expanded />
        </b-field>
      </div>
      <p v-if="structureCount > 0" class="mt-0.5 text-[11px] text-gray-500 tabular-nums">× {{ structureCount }} = <strong class="text-gray-700">{{ cPilings }}</strong></p>
    </div>

    <!-- campo total de hincados (pilotes), es introducido manualmente -->
    <div :class="compact ? 'mb-1' : 'mb-2'">
      <div :class="['font-medium capitalize text-blue truncate', compact ? 'text-xs' : 'text-sm']">{{ compact ? $t('modules') : $t('total_modules') }}</div>
      <div v-if="!compact" class="mb-1 text-xs text-black">(Modulos por estructura)</div>
      <div class="flex flex-row items-center">
        <b-field class="mb-0 w-full"
          :type="{ 'is-danger' : errors.modules } "
          :message="{ 'La cantidad es invalida' : errors.modules  }">
          <b-numberinput
            v-model="form.total_modules"
            controls-position="compact"
            :disabled="disableFields || !createElements.modules"
            size="is-small"
            icon-pack="fas"
            @input="(value) => { errors.modules = value <= 0 }"
            expanded />
        </b-field>
      </div>
      <p v-if="structureCount > 0" class="mt-0.5 text-[11px] text-gray-500 tabular-nums">× {{ structureCount }} = <strong class="text-gray-700">{{ cModules }}</strong></p>
    </div>

    <!-- campo total de paneles, es introducido manualmente -->
    <div :class="compact ? 'mb-1' : 'mb-2'">
      <div :class="['font-medium capitalize text-blue truncate', compact ? 'text-xs' : 'text-sm']">{{ compact ? $t('panels') : $t('total_panels') }}</div>
      <div v-if="!compact" class="mb-1 text-xs text-black">(Paneles por modulo)</div>
      <div class="flex flex-row items-center">
        <b-field class="mb-0 w-full"
          :type="{ 'is-danger' : errors.panels } "
          :message="{ 'La cantidad es invalida' : errors.panels  }">
          <b-numberinput
            v-model="form.total_panels"
            controls-position="compact"
            :disabled="disableFields || !createElements.modules"
            size="is-small"
            icon-pack="fas"
            @input="(value) => { errors.panels = value <= 0 }"
            expanded />
        </b-field>
      </div>
      <p v-if="structureCount > 0" class="mt-0.5 text-[11px] text-gray-500 tabular-nums">× {{ structureCount }} = <strong class="text-gray-700">{{ cPanels }}</strong></p>
    </div>
    </div>
  </div>
</template>

<script>

export default {
  name: "fields-design-form",

  components: {
  },

  props: {
    disableFields: { type: Boolean, default: false },
    // Nº de estructuras del ámbito (total DXF o de la capa). Si > 0, se muestra
    // el total arriba y el conteo resultante debajo de cada input.
    structureCount: { type: Number, default: 0 },
    // Layout compacto (grid 2 col, sin descripciones, títulos pequeños) para
    // listas densas como "por capas". Por defecto el layout amplio de siempre.
    compact: { type: Boolean, default: false },
    createElements: {
      type: Object,
      default: () => ({
        pilings: false,
        modules: false,
      }),
    },
  },

  data: () => ({
    form: {
      total_axles: 1, // por defecto es de un eje la estructura
      total_pilings: 0,
      create_pilings: false,
      total_modules: 0,
      create_modules: false,
      total_panels: 1, // por defecto 1 panel por modulo
    },

    errors: {
      axles: false,
      pilings: false,
      modules: false,
      panels: false,
    },
  }),

  created() {
  },

  computed: {
    // Conteos resultantes = estructuras × valor por estructura (preview).
    cAxles()   { return (this.structureCount || 0) * (Number(this.form.total_axles)   || 0) },
    // Hincados = estructuras × ejes × hincados por eje (2 ejes × 2 hincas = 4/estructura).
    cPilings() { return (this.structureCount || 0) * (Number(this.form.total_axles) || 0) * (Number(this.form.total_pilings) || 0) },
    cModules() { return (this.structureCount || 0) * (Number(this.form.total_modules) || 0) },
    cPanels()  { return this.cModules * (Number(this.form.total_panels) || 0) },
  },

  watch: {
    // Emite los valores del form en cada cambio para que el host (modal) pueda
    // mostrar un resumen en vivo (estructuras × config). Aditivo, no afecta la
    // validación ni getDataForm().
    form: {
      deep: true,
      immediate: true,
      handler(f) {
        this.$emit('epc-change', { ...f })
      },
    },
  },

  methods: {
    /** Siembra el formulario con valores previos (rehidratación). Dispara el
     *  watch → emite epc-change para que el host actualice su resumen. */
    setForm(values) {
      if (!values) return
      this.form = { ...this.form, ...values }
    },

    getDataForm() {
      if (this._ValidateForm()) {
        return this.form
      }

      return null
    },

    _ValidateForm() {
      this.errors.axles = this.form.total_axles < 1 || this.form.total_axles > 3
      this.errors.pilings = this.createPilings && (this.form.total_pilings == null || this.form.total_pilings <= 0)
      this.errors.modules = this.createModules && (this.form.total_modules == null || this.form.total_modules <= 0)
      this.errors.panels = this.createModules && (this.form.total_panels == null || this.form.total_panels <= 0)

      if ( ! this.errors.axles && ! this.errors.pilings && ! this.errors.modules && ! this.errors.panels ) {
        return true
      }

      this.$toast.open({
        message: 'campos marcados obligatorios',
        type: "is-danger",
        position: "is-top-right",
      });

      return false;
    },

  },
};
</script>
